import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

type Args = {
	/** Ids of the currently visible, selectable option rows, in render order. */
	visibleOptionIds: string[];
	onToggle: (id: string) => void;
	onClose: () => void;
	/** True while the search box holds text (Esc clears it first, then closes). */
	hasQuery: boolean;
	clearQuery: () => void;
	/**
	 * Stop every keydown reaching the panel from bubbling further up the React tree (default true). Needed
	 * because `SystemDropdown` is sometimes rendered nested inside a real MUI `<Menu>`/`<MenuList>` (e.g. an
	 * "edit shift" popover living inside a nurse action menu) — React bubbles synthetic events by component
	 * tree, not DOM tree, so even though the panel is portaled elsewhere via `Popper`, an unstopped keydown
	 * still reaches the ancestor `MenuList`'s type-ahead handler and steals focus to a `MenuItem` whose label
	 * starts with the typed letter. Only `preventDefault` is scoped per-key below (arrows/space/etc.) — the
	 * underlying `<input>` must still receive every printable keystroke so search filtering keeps working.
	 * Set to `false` for a consumer that genuinely wants keydowns to bubble past the dropdown.
	 */
	stopPropagationOnKeyDown?: boolean;
};

/**
 * Keyboard model for the panel: Up/Down move the highlight over visible rows, Enter/Space toggle the
 * highlighted row, Esc clears the query (if any) then closes, Home/End jump. Selection changes only
 * touch the highlighted id — never the whole set — so the invariant holds under keyboard use too.
 */
export const useDropdownKeyboard = ({ visibleOptionIds, onToggle, onClose, hasQuery, clearQuery, stopPropagationOnKeyDown = true }: Args) => {
	const [highlightedId, setHighlightedId] = useState<string | null>(null);
	const idsKey = visibleOptionIds.join(" ");
	const idsRef = useRef(visibleOptionIds);
	idsRef.current = visibleOptionIds;

	// Keep the highlight valid as the visible set changes (e.g. while typing): default to the first row.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `idsKey` is the stable string form of the ids; the current set is read from the ref to avoid re-running on every array identity change.
	useEffect(() => {
		const ids = idsRef.current;
		setHighlightedId((current) => (current && ids.includes(current) ? current : (ids[0] ?? null)));
	}, [idsKey]);

	const move = useCallback((delta: number) => {
		const ids = idsRef.current;
		if (ids.length === 0) return;
		setHighlightedId((current) => {
			const index = current ? ids.indexOf(current) : -1;
			const next = Math.min(ids.length - 1, Math.max(0, index + delta));
			return ids[next];
		});
	}, []);

	const onKeyDown = useCallback(
		(event: KeyboardEvent) => {
			// Stop the keydown from bubbling past the panel by default — see `stopPropagationOnKeyDown` doc above.
			// Deliberately no `preventDefault()` here: the underlying <input> (when focus is there) must still
			// receive and render the keystroke, which is what drives search filtering.
			if (stopPropagationOnKeyDown) event.stopPropagation();
			// When focus is in the search box, keys that also edit text (Space, Home/End cursor moves)
			// must behave natively — otherwise typing a space toggles a row instead of entering it,
			// so you can't type a second word. Arrow nav + Enter-to-toggle + Escape still work there.
			const target = event.target as HTMLElement | null;
			const inTextInput = !!target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					move(1);
					break;
				case "ArrowUp":
					event.preventDefault();
					move(-1);
					break;
				case "Home":
					if (inTextInput) break;
					event.preventDefault();
					setHighlightedId(idsRef.current[0] ?? null);
					break;
				case "End":
					if (inTextInput) break;
					event.preventDefault();
					setHighlightedId(idsRef.current.at(-1) ?? null);
					break;
				case " ":
					if (inTextInput) break; // let the space type in the search box
					if (highlightedId) {
						event.preventDefault();
						onToggle(highlightedId);
					}
					break;
				case "Enter":
					if (highlightedId) {
						event.preventDefault();
						onToggle(highlightedId);
					}
					break;
				case "Escape":
					event.preventDefault();
					if (hasQuery) clearQuery();
					else onClose();
					break;
			}
		},
		[move, highlightedId, onToggle, onClose, hasQuery, clearQuery, stopPropagationOnKeyDown],
	);

	return useMemo(() => ({ highlightedId, setHighlightedId, onKeyDown }), [highlightedId, onKeyDown]);
};
